<template>
  <Teleport to="body">
    <Transition name="pin-mgr">
      <div v-if="isOpen" class="pin-mgr-overlay" @click.self="handleClose">
        <div class="pin-mgr-panel" role="dialog" aria-modal="true" aria-labelledby="pin-mgr-title">
          <div class="pin-mgr-header">
            <div class="pin-mgr-header-left">
              <span class="pin-mgr-header-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="12" y1="17" x2="12" y2="22" />
                  <path
                    d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"
                  />
                </svg>
              </span>
              <div>
                <h5 id="pin-mgr-title">PINNED SECTIONS</h5>
                <p>Reorder or remove your pinned sections</p>
              </div>
            </div>
            <button class="pin-mgr-close-btn" @click="handleClose" aria-label="Close">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="pin-mgr-body">
            <div v-if="sections.length === 0" class="pin-mgr-empty">
              <i class="fa fa-star-o pin-mgr-empty-icon"></i>
              <p>NO PINNED SECTIONS YET</p>
              <span>Click the options menu on any section to pin it</span>
            </div>
            <ul v-else class="pin-mgr-list">
              <li v-for="(section, idx) in sections" :key="section.id" class="pin-mgr-item">
                <span class="pin-mgr-item-name">{{ getTranslation(section.title) }}</span>
                <div class="pin-mgr-item-controls">
                  <button
                    class="pin-mgr-ctrl-btn"
                    :disabled="idx === 0"
                    title="Move up"
                    @click="$emit('move', { sectionId: section.id, direction: 'up' })"
                  >
                    <i class="fa fa-chevron-up"></i>
                  </button>
                  <button
                    class="pin-mgr-ctrl-btn"
                    :disabled="idx === sections.length - 1"
                    title="Move down"
                    @click="$emit('move', { sectionId: section.id, direction: 'down' })"
                  >
                    <i class="fa fa-chevron-down"></i>
                  </button>
                  <button
                    class="pin-mgr-ctrl-btn pin-mgr-remove-btn"
                    title="Remove from pinned sections"
                    @click="$emit('toggle-favorite', section.id)"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </li>
            </ul>
          </div>

          <div class="pin-mgr-footer">
            <button class="pin-mgr-done-btn" @click="handleClose">DONE</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  sections: {
    type: Array,
    required: true,
  },
  getTranslation: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['close', 'move', 'toggle-favorite']);

function handleClose() {
  emit('close');
}
</script>

<style lang="scss" scoped>
/* ── Manage Pinned Sections Modal ── */
.pin-mgr-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 14, 40, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.pin-mgr-panel {
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 24px 60px rgba(10, 14, 40, 0.18),
    0 4px 16px rgba(10, 14, 40, 0.1);
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pin-mgr-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 20px;
  border-bottom: 1px solid rgba(67, 74, 115, 0.1);
}

.pin-mgr-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.pin-mgr-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(245, 180, 0, 0.2), rgba(245, 180, 0, 0.08));
  color: #b7791f;
  font-size: 18px;
  flex-shrink: 0;

  svg {
    width: 60%;
  }
}

.pin-mgr-header h5 {
  margin: 0 0 3px;
  font-size: 17px;
  font-weight: 700;
  color: #1a1f3c;
  letter-spacing: -0.01em;
}

.pin-mgr-header p {
  margin: 0;
  font-size: 12.5px;
  color: rgba(67, 74, 115, 0.6);
}

.pin-mgr-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(67, 74, 115, 0.07);
  border-radius: 8px;
  color: #434a73;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.pin-mgr-close-btn:hover {
  background: rgba(192, 57, 43, 0.1);
  color: #c0392b;
}

.pin-mgr-body {
  padding: 16px 24px;
  flex: 1;
  max-height: 420px;
  overflow-y: auto;
}

.pin-mgr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  gap: 8px;
  text-align: center;
}

.pin-mgr-empty-icon {
  font-size: 32px;
  color: rgba(67, 74, 115, 0.25);
  margin-bottom: 4px;
}

.pin-mgr-empty p {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #434a73;
}

.pin-mgr-empty span {
  font-size: 12.5px;
  color: rgba(67, 74, 115, 0.5);
}

.pin-mgr-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pin-mgr-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border-radius: 12px;
  background: rgba(67, 74, 115, 0.04);
  border: 1px solid rgba(67, 74, 115, 0.08);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.pin-mgr-item:hover {
  background: rgba(67, 74, 115, 0.07);
  border-color: rgba(67, 74, 115, 0.15);
}

.pin-mgr-item-name {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #1a1f3c;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: uppercase;
}

.pin-mgr-item-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.pin-mgr-ctrl-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(67, 74, 115, 0.12);
  background: #ffffff;
  border-radius: 8px;
  color: #434a73;
  font-size: 10px;
  cursor: pointer;
  padding: 0;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}

.pin-mgr-ctrl-btn:hover:not(:disabled) {
  background: rgba(67, 74, 115, 0.08);
  border-color: rgba(67, 74, 115, 0.25);
  color: #1a1f3c;
}

.pin-mgr-ctrl-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

.pin-mgr-remove-btn {
  color: #c0392b;
  border-color: rgba(192, 57, 43, 0.15);
}

.pin-mgr-remove-btn:hover:not(:disabled) {
  background: rgba(192, 57, 43, 0.08);
  border-color: rgba(192, 57, 43, 0.3);
  color: #922b21;
}

.pin-mgr-footer {
  padding: 16px 24px 20px;
  border-top: 1px solid rgba(67, 74, 115, 0.1);
  display: flex;
  justify-content: flex-end;
}

.pin-mgr-done-btn {
  padding: 9px 28px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #434a73, #21245e);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition:
    opacity 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 4px 12px rgba(67, 74, 115, 0.25);
}

.pin-mgr-done-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(67, 74, 115, 0.3);
}

.pin-mgr-enter-active,
.pin-mgr-leave-active {
  transition: opacity 0.22s ease;
}

.pin-mgr-enter-active .pin-mgr-panel,
.pin-mgr-leave-active .pin-mgr-panel {
  transition:
    transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 0.22s ease;
}

.pin-mgr-enter-from,
.pin-mgr-leave-to {
  opacity: 0;
}

.pin-mgr-enter-from .pin-mgr-panel,
.pin-mgr-leave-to .pin-mgr-panel {
  transform: scale(0.92) translateY(12px);
  opacity: 0;
}
</style>
